import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { useDetailsPanelRect } from "@/components/canvas/detailsSide/DetailsPanelContext";
import { useLeftPanelRect } from "@/components/canvas/leftSide/LeftPanelContext";
import { TooltipButton } from "components/common/TooltipButton";
import { NumberCount } from "components/common/NumberCount";
import { TruncatedText } from "components/common/TruncatedText";
import { BAR_GAP } from "@/components/canvas/overlays/barGap";
import { useEscapeStack } from "components/hooks/useEscapeStack";

/** Gap kept between an anchored overlay's bottom edge and the viewport bottom. */
const VIEWPORT_MARGIN = 16;

let overlayZIndexCounter = 100;
const nextOverlayZ = () => ++overlayZIndexCounter;

export interface DetailsOverlayProps {
  title: string;
  titleIcon?: React.ReactNode;
  titleExtra?: React.ReactNode;
  count?: number;
  headerActions?: React.ReactNode;
  onClose: () => void;
  children: React.ReactNode;
  width?: number;
  topOffset?: number;
  onHeight?: (height: number) => void;
  /** `"above"` anchors to an arbitrary element rather than a details panel —
   *  the canvas action bar's slots. It shares none of the panel-top clamp.
   *  `"inline"` is not an overlay at all: a panel in the flow that fills its
   *  container, for a host that makes room for it. */
  side?: "right-of" | "left-of" | "above" | "inline";
  maxHeight?: number | string;
  focusKey?: string | number;
  /** Align the overlay's top with this element instead of the panel top, and
   *  follow it as it scrolls. Shifted up only as far as needed to keep the
   *  bottom on screen, and never above the panel top. */
  anchorTo?: HTMLElement | null;
  /** Extra px to shift further left, for stacking a second overlay flush
   *  against one that is itself flush against the details panel. */
  offsetFromPanel?: number;
  /** Drop the header bar and float the close button over the content, keeping
   *  it at the same coordinates the header's button occupied. For content that
   *  supplies its own heading: `title` stops being visible and becomes the
   *  overlay's accessible name instead.
   *
   *  Renders NO other header slot — `titleIcon`, `count`, `titleExtra` and
   *  `headerActions` are all dropped, since there is no bar to put them in. */
  floatingClose?: boolean;
  /** No close button: the content ends itself, as the filter's Cancel does. */
  closable?: boolean;
}

export function DetailsOverlay({
  title,
  titleIcon,
  titleExtra,
  count,
  headerActions,
  onClose,
  children,
  width = 280,
  topOffset = 0,
  onHeight,
  side = "right-of",
  maxHeight,
  focusKey,
  anchorTo,
  offsetFromPanel = 0,
  floatingClose = false,
  closable = true,
}: DetailsOverlayProps) {
  const detailsPanelRect = useDetailsPanelRect();
  const leftPanelRect = useLeftPanelRect();
  const ref = useRef<HTMLDivElement>(null);
  const [zIndex, setZIndex] = useState(() => nextOverlayZ());
  const isInitialFocusKey = useRef(true);
  useEscapeStack(true, onClose);

  useEffect(() => {
    if (focusKey === undefined) return;
    if (isInitialFocusKey.current) {
      isInitialFocusKey.current = false;
      return;
    }
    setZIndex(nextOverlayZ());
  }, [focusKey]);

  useEffect(() => {
    if (!onHeight || !ref.current) return;
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(([entry]) =>
      onHeight(entry.borderBoxSize[0]?.blockSize ?? entry.contentRect.height),
    );
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, [onHeight]);

  const basePanelTop = (side === "left-of" ? leftPanelRect : detailsPanelRect)
    ?.top;
  const [measured, setMeasured] = useState<{
    top: number;
    left: number;
    right: number;
    ownHeight: number;
  } | null>(null);

  // Capture-phase scroll: the details panel's inner scrollers emit events that
  // don't bubble. A layout effect, so the first paint is already in place.
  useLayoutEffect(() => {
    if (!anchorTo) return;
    const measure = () => {
      // Virtualized lists unmount rows scrolled well out of view; hold the last
      // position rather than snapping back to the panel top.
      if (!anchorTo.isConnected) return;
      const r = anchorTo.getBoundingClientRect();
      // Above the bar, the gap is from the bar's edge, not the slot's inset in it.
      const edge =
        side === "above"
          ? anchorTo.closest<HTMLElement>(".canvas-action-toolbar")
          : null;
      const top = (edge ?? anchorTo).getBoundingClientRect().top;
      const ownHeight = ref.current?.offsetHeight ?? 0;
      // A fresh object on every nested scroll event would defeat React's
      // bail-out and repaint the overlay on each one.
      setMeasured((prev) =>
        prev &&
        prev.top === top &&
        prev.left === r.left &&
        prev.right === r.right &&
        prev.ownHeight === ownHeight
          ? prev
          : { top, left: r.left, right: r.right, ownHeight },
      );
    };
    measure();
    const ro =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(measure)
        : null;
    ro?.observe(anchorTo);
    if (ref.current) ro?.observe(ref.current);
    window.addEventListener("scroll", measure, true);
    window.addEventListener("resize", measure);
    return () => {
      ro?.disconnect();
      window.removeEventListener("scroll", measure, true);
      window.removeEventListener("resize", measure);
    };
  }, [anchorTo, side]);
  const anchorRect = anchorTo ? measured : null;

  // Beside a panel, the anchor aligns the top, clamped so the bottom stays on
  // screen and the top never rises above the panel.
  const anchoredTop =
    anchorRect && side !== "above"
      ? Math.round(
          Math.max(
            (basePanelTop ?? 80) + topOffset,
            Math.min(
              anchorRect.top,
              window.innerHeight - anchorRect.ownHeight - VIEWPORT_MARGIN,
            ),
          ),
        )
      : null;

  const explicitMax =
    maxHeight !== undefined
      ? typeof maxHeight === "number"
        ? `${maxHeight}px`
        : maxHeight
      : null;
  const resolveMaxHeight = (overlayTop: number) => {
    const viewportCap = `calc(100vh - ${overlayTop + 16}px)`;
    return explicitMax ? `min(${explicitMax}, ${viewportCap})` : viewportCap;
  };

  let posStyle: React.CSSProperties;
  let slideInClass: string;
  if (side === "above") {
    // Positioned by its BOTTOM edge, so the overlay never needs its own height
    // to sit a fixed gap above the slot.
    const anchorTop = anchorRect?.top ?? window.innerHeight;
    const roomAbove = Math.max(0, anchorTop - BAR_GAP - VIEWPORT_MARGIN);
    posStyle = {
      bottom: window.innerHeight - anchorTop + BAR_GAP,
      left: anchorRect
        ? Math.round(
            Math.max(
              VIEWPORT_MARGIN,
              Math.min(
                (anchorRect.left + anchorRect.right - width) / 2,
                window.innerWidth - width - VIEWPORT_MARGIN,
              ),
            ),
          )
        : VIEWPORT_MARGIN,
      maxHeight: explicitMax
        ? `min(${explicitMax}, ${roomAbove}px)`
        : `${roomAbove}px`,
    };
    slideInClass = "slide-in-from-bottom-1";
  } else if (side === "left-of") {
    const overlayTop =
      anchoredTop ?? (leftPanelRect ? leftPanelRect.top : 80) + topOffset;
    posStyle = leftPanelRect
      ? {
          left: leftPanelRect.right,
          top: overlayTop,
          maxHeight: resolveMaxHeight(overlayTop),
        }
      : {
          left: 240,
          top: overlayTop,
          maxHeight: resolveMaxHeight(overlayTop),
        };
    slideInClass = "slide-in-from-left-1";
  } else if (side === "inline") {
    posStyle = {};
    slideInClass = "";
  } else {
    const overlayTop =
      anchoredTop ?? (detailsPanelRect ? detailsPanelRect.top : 80) + topOffset;
    posStyle = detailsPanelRect
      ? {
          right: window.innerWidth - detailsPanelRect.left + offsetFromPanel,
          top: overlayTop,
          maxHeight: resolveMaxHeight(overlayTop),
        }
      : {
          right: 240 + offsetFromPanel,
          top: overlayTop,
          maxHeight: resolveMaxHeight(overlayTop),
        };
    slideInClass = "slide-in-from-right-1";
  }

  return (
    <div
      ref={ref}
      className={
        side === "inline"
          ? "flex flex-col h-full min-h-0 bg-bg-subtle border border-border rounded-app"
          : `fixed flex flex-col bg-bg-subtle border border-border rounded-app shadow-lg animate-in ${slideInClass} duration-100`
      }
      style={side === "inline" ? undefined : { ...posStyle, width, zIndex }}
      onClick={(e) => {
        e.stopPropagation();
        // Raise only when covered, so clicks never climb past the dialog layer.
        if (zIndex !== overlayZIndexCounter) setZIndex(nextOverlayZ());
      }}
      // A bare `div` exposes no accessible name, so the label needs a role to
      // land on. Non-modal dialog: the overlay is dismissible and sits beside
      // the content it annotates rather than trapping focus.
      role={floatingClose ? "dialog" : undefined}
      aria-label={floatingClose ? title : undefined}
    >
      {floatingClose ? (
        <div className="absolute top-2 right-3 z-10">
          <TooltipButton
            title="Close"
            aria-label="Close"
            variant="default"
            size="xs"
            className="icon-button-glow-hover"
            onClick={onClose}
          >
            <CloseIcon />
          </TooltipButton>
        </div>
      ) : (
        <div className="shrink-0 flex items-center gap-2 px-3 py-2 border-b border-border select-none">
          <div className="flex-1 min-w-0 flex items-center gap-2">
            {titleIcon}
            <TruncatedText className="text-fg-semibold min-w-0 cursor-default">
              {title}
            </TruncatedText>
            {count !== undefined && <NumberCount count={count} size="sm" />}
            {titleExtra}
          </div>
          {headerActions}
          {closable && (
            <TooltipButton
              title="Close"
              aria-label="Close"
              variant="default"
              size="xs"
              onClick={onClose}
            >
              <CloseIcon />
            </TooltipButton>
          )}
        </div>
      )}
      <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
        {children}
      </div>
    </div>
  );
}
